import React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { RichTextDisplay } from '@factorialco/f0-react/dist/experimental'
import {
  RemoveUploadDocumentTaskFileDocument,
  TaskActivityDocument,
  UploadDocumentTaskFilesDocument,
} from 'generated/resources/graphql'

import DropZone from 'modules/core/components/DropZone'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import DropItem from 'modules/procurement/components/DropItem'

interface UploadDocumentProps {
  content?: string | null
  uploadDocumentTaskId: number
  files?: {
    id: number
    fileId: number
    byteSize: number
    contentType: string
    filename: string
    url: string
  }[]
}

export const UploadDocument = ({ content, uploadDocumentTaskId, files }: UploadDocumentProps) => {
  const [uploadFiles, { loading: isUploading }] = useMutation(UploadDocumentTaskFilesDocument, {
    extractorFn: (data) => data.procurementMutations.uploadProcurementUploadDocumentTask,
    refetchQueries: [TaskActivityDocument],
  })

  const [removeFile] = useMutation(RemoveUploadDocumentTaskFileDocument, {
    extractorFn: (data) => data.procurementMutations.removeFileProcurementUploadDocumentTask,
    refetchQueries: [TaskActivityDocument],
  })

  const handleOnDrop = React.useCallback(
    async (acceptedFiles: File[]) => {
      if (!uploadDocumentTaskId) return

      const result = await uploadFiles({
        variables: {
          files: acceptedFiles,
          uploadDocumentTaskId,
        },
      })

      const handled = handleResult(result, () => undefined)

      if (!handled.success) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t(
            'tasks.types.procurement_request_document.upload_document.errors.upload_error'
          ),
        })
      }
    },
    [uploadDocumentTaskId, uploadFiles]
  )

  const handleFileRemove = React.useCallback(
    async (fileId: number) => {
      if (!uploadDocumentTaskId || !fileId) return

      const result = await removeFile({
        variables: {
          fileId: fileId,
          uploadDocumentTaskId,
        },
      })

      const handled = handleResult(result, () => {})

      if (!handled.success) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t(
            'tasks.types.procurement_request_document.upload_document.errors.remove_file_failed'
          ),
        })
      }
    },
    [uploadDocumentTaskId, removeFile]
  )
  return (
    <Box gap="s12">
      <Text.Header3>
        {i18n.t('tasks.types.procurement_request_document.required_action_text_label')}
      </Text.Header3>
      {content && (
        <Box flexDirection="column" gap="s4">
          <RichTextDisplay content={content} className="text-f1-foreground-secondary" />
        </Box>
      )}

      <DropZone
        onDrop={handleOnDrop}
        accept={{ 'application/pdf': [], 'image/jpeg': [], 'image/png': [] }}
        multiple
        loading={isUploading}
        placeholder={i18n.t('tasks.types.procurement_request_document.dropzone_placeholder')}
      />
      {files && files.length > 0 && (
        <Box flexDirection="column" gap="s8">
          {files.map((file) => {
            return (
              <DropItem
                key={file.fileId}
                file={file}
                onRemove={() => handleFileRemove(file.fileId)}
              />
            )
          })}
        </Box>
      )}
    </Box>
  )
}
